import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { AppSelect } from "../../components/AppSelect";
import { DatasetBreadcrumbNav } from "../../components/DatasetBreadcrumbNav";
import {
  activateVersion, cancelProcessingJob, createDraftFromRevision, dataKeys, getActivation, getContext, getHistory,
  getMaterialization, getPublication, getRevisions, getSources, getValidation,
  getVersions, initializeDataManagement, launchProcessing, materializeActive, openDataDraft,
  publishVersion, safeErrorMessage, type DatasetLocation,
} from "./api";
import type { CandidateVersion, DataManagementContext, DataManagementState, JobHistoryItem, MaterializationJob, SourceRevision } from "./types";
import { SourceExplorer } from "./SourceExplorer";
import { SourceRevisionExplorer } from "./SourceRevisionExplorerPage";
import {
  fullPipelineErrorMessage,
  fullPipelineStepLabel,
  runFullDataPipeline,
  type FullPipelineStep,
} from "./fullDataPipeline";
import { ACTIVE_JOB_STATES, useDatasetDataManagementLiveState } from "./useDatasetDataManagementLiveState";
import { ValidationReportView } from "./ValidationReportView";

const SECTIONS = [
  ["overview", "Vue générale"], ["sources", "Sources"], ["validation", "Validation"],
  ["semanticization", "Sémantisation"], ["versions", "Versions"],
  ["publication", "Publication"], ["activation", "Activation et matérialisation"],
  ["jobs", "Historique des jobs"],
] as const;
type Section = typeof SECTIONS[number][0];
type PipelineTiming = { startedAt: number; durationMs: number | null };
type PipelineTimings = Partial<Record<FullPipelineStep, PipelineTiming>>;

const WORKFLOW_STEPS: Array<{ id: string; label: string; section: Section }> = [
  { id: "sources", label: "Sources", section: "sources" },
  { id: "validation", label: "Validation", section: "validation" },
  { id: "semanticization", label: "Sémantisation", section: "semanticization" },
  { id: "publication", label: "Publication", section: "publication" },
  { id: "activation", label: "Activation", section: "activation" },
  { id: "materialization", label: "Matérialisation", section: "activation" },
];
const PIPELINE_STEPS: FullPipelineStep[] = [
  "validation", "semanticization", "publication", "activation", "materialization",
];

const COMPLEMENTARY_SECTIONS: Array<{ id: Section; label: string }> = [
  { id: "overview", label: "Vue générale" },
  { id: "versions", label: "Versions" },
  { id: "jobs", label: "Historique des jobs" },
];

export function DatasetDataManagementPage() {
  const { clientId, datasetId } = useParams<{ clientId: string; datasetId: string }>();
  if (!clientId || !datasetId) return <PageState message="Dataset introuvable." />;
  return <div className="dataset-detail-page dd-page-wrapper">
    <DatasetBreadcrumbNav
      clientId={clientId}
      clientLabel={clientId}
      datasetId={datasetId}
      datasetLabel={datasetId}
      currentLabel="Gestion des données"
    />
    <DataManagementContent location={{ clientId, datasetId }} />
  </div>;
}

function DataManagementContent({ location }: { location: DatasetLocation }) {
  const queryClient = useQueryClient();
  const initializationInFlight = useRef(false);
  const contextQuery = useQuery({
    queryKey: dataKeys.context(location),
    queryFn: () => getContext(location),
    staleTime: 30_000,
  });
  const initializeMutation = useMutation({
    mutationFn: () => initializeDataManagement(location),
    onSuccess: (state) => queryClient.setQueryData(dataKeys.context(location), state),
    onSettled: () => { initializationInFlight.current = false; },
  });

  if (contextQuery.isLoading) return <PageState message="Chargement de la gestion des données…" />;
  if (contextQuery.error || !contextQuery.data) return <PageState message={safeErrorMessage(contextQuery.error)} error />;
  if (contextQuery.data.state === "initialized") {
    return <InitializedDataManagementContent location={location} context={contextQuery.data.context} />;
  }

  const initialize = () => {
    if (initializationInFlight.current) return;
    if (!window.confirm("Initialiser la gestion moderne de ce dataset ?")) return;
    initializationInFlight.current = true;
    initializeMutation.mutate();
  };
  return <LegacyInitialization
    state={contextQuery.data}
    pending={initializeMutation.isPending}
    error={initializeMutation.error}
    onInitialize={initialize}
  />;
}

function InitializedDataManagementContent({ location, context }: {
  location: DatasetLocation;
  context: DataManagementContext;
}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = validSection(searchParams.get("section"));
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [pipelineStep, setPipelineStep] = useState<FullPipelineStep | null>(null);
  const [pipelineTimings, setPipelineTimings] = useState<PipelineTimings>({});
  const draftOpening = useRef(false);
  const handleLiveNotification = useCallback((message: string) => setNotice(message), []);
  const canAdmin = context.canAdmin;
  const sourcesQuery = useQuery({
    queryKey: dataKeys.sources(location),
    queryFn: () => getSources(location),
    enabled: Boolean(context.draft),
    staleTime: 10_000,
  });
  const revisionsQuery = useQuery({ queryKey: dataKeys.revisions(location), queryFn: () => getRevisions(location), staleTime: 30_000 });
  const validationQuery = useQuery({
    queryKey: dataKeys.validation(location), queryFn: () => getValidation(location), staleTime: 10_000,
    enabled: Boolean(context.draft),
  });
  const versionsQuery = useQuery({ queryKey: dataKeys.versions(location), queryFn: () => getVersions(location), staleTime: 10_000 });
  const activationQuery = useQuery({ queryKey: dataKeys.activation(location), queryFn: () => getActivation(location), staleTime: 10_000 });
  const materializationQuery = useQuery({ queryKey: dataKeys.materialization(location), queryFn: () => getMaterialization(location), staleTime: 10_000 });
  const liveState = useDatasetDataManagementLiveState({
    location,
    onNotification: handleLiveNotification,
  });
  const jobsQuery = liveState.jobsQuery;
  const historyQuery = useQuery({ queryKey: dataKeys.history(location), queryFn: () => getHistory(location), staleTime: 30_000 });

  const versions = versionsQuery.data ?? [];
  const selectedVersion = versions.find((item) => item.id === selectedVersionId) ?? versions[0] ?? null;
  const publicationVersions = versions.filter((version) => {
    return version.status === "candidate" || version.status === "published";
  });
  const selectedPublicationVersion = publicationVersions.find((item) => item.id === selectedVersionId)
    ?? publicationVersions[0]
    ?? null;
  const publicationQuery = useQuery({
    queryKey: dataKeys.publication(location, selectedPublicationVersion?.id ?? "none"),
    queryFn: () => getPublication(location, selectedPublicationVersion!.id),
    enabled: Boolean(selectedPublicationVersion),
    staleTime: 5_000,
  });
  const readySources = sourcesQuery.data?.sources.filter((source) => source.status === "ready") ?? [];
  const hasReadyRevision = Boolean(
    context.draft && (sourcesQuery.data?.readiness?.ready ?? readySources.length > 0),
  );
  const pipelineRunning = pipelineStep !== null;
  const terminalDraft = isTerminalDraftStatus(context.draft?.status);
  const restartRevision = terminalDraft
    ? findDraftRevision(revisionsQuery.data ?? [], context.draft)
    : null;
  const completedWorkflow = {
    sources: hasReadyRevision || Boolean(restartRevision),
    validation: validationQuery.data?.usable === true,
    semanticization: versions.some((version) => ["candidate", "publishing", "published"].includes(version.status)),
    publication: versions.some((version) => version.status === "published"),
    activation: Boolean(activationQuery.data?.activeVersion),
    materialization: materializationQuery.data?.aligned === true,
  };
  const restartFromValidation = completedWorkflow.validation || completedWorkflow.semanticization;
  const canRunCompleteWorkflow = hasReadyRevision || Boolean(restartRevision);
  const displayedWorkflow = pipelineStep
    ? workflowCompletionDuringRun(completedWorkflow, pipelineStep)
    : completedWorkflow;

  const openDraftMutation = useMutation({
    mutationFn: () => openDataDraft(location),
    onSuccess: ({ draft }) => {
      queryClient.setQueryData<DataManagementState>(dataKeys.context(location), (current) => {
        if (!current || current.state !== "initialized") return current;
        return { ...current, context: { ...current.context, draft } };
      });
      setNotice("La révision en préparation est prête. Vous pouvez sélectionner les fichiers raw.");
    },
    onSettled: () => { draftOpening.current = false; },
  });

  const refreshDataManagementState = liveState.refreshDataManagementState;

  const runMutation = useMutation({
    mutationFn: async (input: { kind: "validate" | "semanticize" | "publish" | "activate" | "materialize"; versionId?: string }) => {
      if (input.kind === "validate" || input.kind === "semanticize") return await launchProcessing(location, input.kind);
      if (input.kind === "publish" && input.versionId) return await publishVersion(location, input.versionId);
      if (input.kind === "activate" && input.versionId) return await activateVersion(location, input.versionId);
      if (input.kind === "materialize") return await materializeActive(location);
      throw new Error("Action incomplète.");
    },
    onMutate: () => { setActionError(""); setNotice(""); },
    onSuccess: async (_result, input) => {
      setNotice(actionStartedMessage(input.kind, materializationQuery.data?.aligned === true));
      await refreshDataManagementState();
    },
    onError: (error) => setActionError(safeErrorMessage(error)),
  });
  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => cancelProcessingJob(location, jobId),
    onSuccess: refreshDataManagementState,
    onError: (error) => setActionError(safeErrorMessage(error)),
  });
  const activeValidationJob = jobsQuery.data?.items.find((job) => {
    return job.type === "validate" && ACTIVE_JOB_STATES.has(job.state);
  }) ?? null;

  const selectSection = (next: Section) => setSearchParams(next === "overview" ? {} : { section: next });
  const openFirstDraft = () => {
    if (draftOpening.current) return;
    draftOpening.current = true;
    setActionError("");
    openDraftMutation.mutate();
  };
  const submitAction = (kind: "validate" | "semanticize" | "publish" | "activate" | "materialize", versionId?: string) => {
    if (runMutation.isPending || pipelineRunning) return;
    const confirmation = kind === "materialize"
      ? materializationConfirmation(materializationQuery.data)
      : confirmationMessage(kind);
    if (requiresConfirmation(kind) && !window.confirm(confirmation)) return;
    runMutation.mutate({ kind, versionId });
  };
  const runCompleteWorkflow = () => {
    if (pipelineRunning || runMutation.isPending || liveState.activeJobs.length || !canRunCompleteWorkflow) return;
    const confirmation = restartFromValidation
      ? "Recommencer depuis la validation ? Les sources et les anciennes versions seront conservées."
      : "Lancer validation, sémantisation, publication, activation et matérialisation à la suite ?";
    if (!window.confirm(confirmation)) return;
    setActionError("");
    setNotice("");
    setPipelineTimings({ validation: { startedAt: Date.now(), durationMs: null } });
    setPipelineStep("validation");
    const updatePipelineStep = (nextStep: FullPipelineStep) => {
      const changedAt = Date.now();
      setPipelineTimings((current) => finishCurrentTiming(current, nextStep, changedAt));
      setPipelineStep(nextStep);
    };
    const run = async () => {
      if (terminalDraft) {
        if (!restartRevision?.id) throw new Error("La révision de sources à reprendre est introuvable.");
        await createDraftFromRevision(location, restartRevision.id);
      }
      return await runFullDataPipeline(location, updatePipelineStep);
    };
    void run().then((versionId) => {
      setSelectedVersionId(versionId);
      setNotice("Processus complet terminé : la nouvelle version est active et matérialisée.");
    }).catch((error) => {
      setActionError(fullPipelineErrorMessage(error));
    }).finally(() => {
      const finishedAt = Date.now();
      setPipelineTimings((current) => finishCurrentTiming(current, null, finishedAt));
      setPipelineStep(null);
      void refreshDataManagementState();
    });
  };
  const actionsBusy = runMutation.isPending || pipelineRunning;
  const explorerVisible = section === "sources"
    && searchParams.get("view") !== "summary"
    && Boolean(revisionsQuery.data?.length);

  return (
    <main className={`dm-page${explorerVisible ? " source-explorer-host" : ""}`}>
      <header className="dm-header">
        <div>
          <Link to={`/clients/${encodeURIComponent(location.clientId)}/datasets/${encodeURIComponent(location.datasetId)}`}>← Retour au dataset</Link>
          <p className="dm-eyebrow">{context.client.name}</p>
          <h1>Gestion des données — {context.dataset.name}</h1>
          <p>Sources, validation, sémantisation et mise à disposition des versions.</p>
        </div>
        <Status value={context.canAdmin ? "administrateur" : "contributeur"} />
      </header>

      <WorkflowProgress
        section={section}
        completed={displayedWorkflow}
        onSelect={selectSection}
        showRunAll={canAdmin}
        runAllDisabled={!canRunCompleteWorkflow || Boolean(liveState.activeJobs.length) || runMutation.isPending}
        restartFromValidation={restartFromValidation}
        pipelineStep={pipelineStep}
        pipelineTimings={pipelineTimings}
        onRunAll={runCompleteWorkflow}
      />

      <nav className="dm-tabs" aria-label="Vues complémentaires">
        <span>Vues complémentaires</span>
        {COMPLEMENTARY_SECTIONS.map(({ id, label }) => (
          <button key={id} type="button" aria-current={section === id ? "page" : undefined} onClick={() => selectSection(id)}>{label}</button>
        ))}
      </nav>

      <div className="dm-live-region" aria-live="polite" aria-atomic="true">
        {notice ? <p className="dm-notice" role="status">{notice}</p> : null}
        {liveState.reconnecting ? <p className="dm-reconnecting" role="status">Connexion temporairement interrompue — nouvelle tentative en cours…</p> : null}
      </div>
      {actionError ? <p className="dm-error" role="alert">{actionError}</p> : null}

      {section === "overview" ? <Overview context={context} sources={sourcesQuery.data} validation={validationQuery.data} versions={versions} activation={activationQuery.data} materialization={materializationQuery.data} jobs={jobsQuery.data?.items ?? []} onNavigate={selectSection} /> : null}
      {section === "sources" && (searchParams.get("view") === "summary" || !(revisionsQuery.data?.length)) ? <SourcesPanel location={location} hasDraft={Boolean(context.draft)} openingDraft={openDraftMutation.isPending} openDraftError={openDraftMutation.error} onOpenDraft={openFirstDraft} sourcesQuery={sourcesQuery} revisions={revisionsQuery.data ?? []} onChanged={refreshDataManagementState} onValidate={() => submitAction("validate")} validationBusy={actionsBusy || cancelJobMutation.isPending} activeProcessing={activeValidationJob} onCancelProcessing={() => { if (activeValidationJob) cancelJobMutation.mutate(activeValidationJob.id); }} /> : null}
      {explorerVisible ? <SourceRevisionExplorer location={location} embedded /> : null}
      {section === "validation" ? <ValidationPanel validation={validationQuery.data} activeJob={activeValidationJob} validationRunning={pipelineStep === "validation"} busy={actionsBusy} hasReadyRevision={hasReadyRevision} onNavigate={selectSection} onValidate={() => submitAction("validate")} onCancel={(id) => cancelJobMutation.mutate(id)} /> : null}
      {section === "semanticization" ? <SemanticizationPanel validation={validationQuery.data} currentRevision={context.draft?.sourceRevision ?? null} jobs={jobsQuery.data?.items ?? []} busy={actionsBusy} onNavigate={selectSection} onSemanticize={() => submitAction("semanticize")} onCancel={(id) => cancelJobMutation.mutate(id)} /> : null}
      {section === "versions" ? <VersionsPanel versions={versions} selected={selectedVersion} activation={activationQuery.data} materialization={materializationQuery.data} onSelect={setSelectedVersionId} /> : null}
      {section === "publication" ? <PublicationPanel versions={publicationVersions} selected={selectedPublicationVersion} publication={publicationQuery.data} activeJob={activeJob(jobsQuery.data?.items, "publish")} canAdmin={canAdmin} busy={actionsBusy} onNavigate={selectSection} onSelect={setSelectedVersionId} onPublish={(id) => submitAction("publish", id)} onCancel={(id) => cancelJobMutation.mutate(id)} /> : null}
      {section === "activation" ? <ActivationPanel versions={versions} activation={activationQuery.data} materialization={materializationQuery.data} activeMaterialization={activeJob(jobsQuery.data?.items, "materialize")} canAdmin={canAdmin} busy={actionsBusy} onNavigate={selectSection} onActivate={(id) => submitAction("activate", id)} onMaterialize={() => submitAction("materialize")} onCancel={(id) => cancelJobMutation.mutate(id)} /> : null}
      {section === "jobs" ? <JobsPanel jobs={jobsQuery.data?.items ?? []} history={historyQuery.data?.items ?? []} loading={jobsQuery.isLoading || historyQuery.isLoading} /> : null}
    </main>
  );
}

function LegacyInitialization({ state, pending, error, onInitialize }: {
  state: Extract<DataManagementState, { state: "legacy_not_initialized" }>;
  pending: boolean;
  error: unknown;
  onInitialize: () => void;
}) {
  return <main className="dm-page">
    <header className="dm-header">
      <div>
        <Link to={`/clients/${encodeURIComponent(state.client.id)}/datasets/${encodeURIComponent(state.dataset.id)}`}>← Retour au dataset</Link>
        <p className="dm-eyebrow">{state.client.name}</p>
        <h1>Gestion des données — {state.dataset.name}</h1>
      </div>
      <Status value="dataset historique" />
    </header>
    <section className="dm-panel">
      <h2>Gestion moderne non initialisée</h2>
      <p>Ce dataset existe dans le catalogue historique, mais il n’est pas encore enregistré dans la gestion moderne des données.</p>
      <p>Son initialisation ne crée, n’active et ne matérialise aucune version.</p>
      {state.canInitialize ? <button className="btn btn-primary" type="button" disabled={pending} onClick={onInitialize}>{pending ? "Initialisation en cours…" : "Initialiser la gestion des données"}</button> : <p role="status">Seul un administrateur autorisé peut effectuer cette initialisation.</p>}
      {error ? <p className="dm-error" role="alert">{safeErrorMessage(error)}</p> : null}
    </section>
  </main>;
}

function WorkflowProgress({
  section, completed, onSelect, showRunAll, runAllDisabled, restartFromValidation,
  pipelineStep, pipelineTimings, onRunAll,
}: {
  section: Section;
  completed: Record<string, boolean>;
  onSelect: (section: Section) => void;
  showRunAll: boolean;
  runAllDisabled: boolean;
  restartFromValidation: boolean;
  pipelineStep: FullPipelineStep | null;
  pipelineTimings: PipelineTimings;
  onRunAll: () => void;
}) {
  const now = useCurrentTime(pipelineStep !== null);
  return <nav className="dm-workflow" aria-label="Workflow principal">
    <div className="dm-workflow-heading">
      <div className="dm-workflow-title">
        <strong>Workflow principal</strong>
        <span>Suivez les étapes dans l’ordre.</span>
      </div>
      {showRunAll ? <button
        className="btn btn-primary dm-run-all"
        type="button"
        disabled={runAllDisabled || pipelineStep !== null}
        onClick={onRunAll}
      >
        {fullPipelineStepLabel(pipelineStep, restartFromValidation)}
      </button> : null}
    </div>
    <ol>
      {WORKFLOW_STEPS.map((step, index) => {
        const timing = pipelineTimings[step.id as FullPipelineStep];
        let current = section === step.section;
        if (section === "activation" && step.id === "activation") {
          current = !completed.activation;
        }
        if (section === "activation" && step.id === "materialization") {
          current = completed.activation;
        }
        return <li key={step.id} className={completed[step.id] ? "is-complete" : ""}>
          <button type="button" aria-current={current ? "step" : undefined} onClick={() => onSelect(step.section)}>
            <span className="dm-workflow-number" aria-hidden="true">{completed[step.id] ? "✓" : index + 1}</span>
            <span className="dm-workflow-step-label">
              <span>{step.label}</span>
              {timing ? <small>{formatDuration(timing.durationMs ?? now - timing.startedAt)}</small> : null}
            </span>
          </button>
        </li>;
      })}
    </ol>
  </nav>;
}

function Overview({ context, sources, validation, versions, activation, materialization, jobs, onNavigate }: {
  context: DataManagementContext; sources: Awaited<ReturnType<typeof getSources>> | undefined;
  validation: Awaited<ReturnType<typeof getValidation>> | undefined; versions: CandidateVersion[];
  activation: Awaited<ReturnType<typeof getActivation>> | undefined;
  materialization: Awaited<ReturnType<typeof getMaterialization>> | undefined; jobs: JobHistoryItem[];
  onNavigate: (section: Section) => void;
}) {
  const candidate = versions[0];
  const published = versions.find((version) => version.status === "published");
  const semanticization = jobs.find((job) => job.type === "semanticize");
  return <section className="dm-panel" aria-labelledby="overview-title">
    <h2 id="overview-title">Vue générale</h2>
    <div className="dm-summary-grid">
      <Summary label="Template" value={context.dataset.templateId} />
      <Summary label="Sources" value={sources ? `${sources.sources.length}` : "Réservé aux administrateurs"} />
      <Summary label="Dernière révision" value={context.draft ? `r${context.draft.sourceRevision}` : "Aucune"} />
      <Summary label="Validation" value={validation?.status ?? context.lastValidation?.status ?? "Non demandée"} />
      <Summary label="Sémantisation" value={semanticization?.state ?? "Non demandée"} />
      <Summary label="Candidate récente" value={candidate ? `v${candidate.versionNumber} · ${candidate.status}` : "Aucune"} />
      <Summary label="Publication récente" value={published ? `v${published.versionNumber}` : "Aucune"} />
      <Summary label="Version active" value={activation?.activeVersion ? `v${activation.activeVersion.versionNumber}` : "Aucune"} />
      <Summary label="Version matérialisée" value={materialization?.materializedVersion?.versionNumber ? `v${materialization.materializedVersion.versionNumber}` : "Aucune"} />
      <Summary label="Alignement" value={alignmentLabel(materialization)} />
      <Summary label="Dernier job" value={jobs[0] ? `${jobs[0].type} · ${jobs[0].state}` : "Aucun"} />
    </div>
    {!context.draft ? <EmptyState
      title="Aucune révision en préparation"
      explanation="Commencez par ajouter les fichiers raw nécessaires au dataset."
      actionLabel="Aller aux sources"
      onAction={() => onNavigate("sources")}
    /> : null}
    {!materialization?.aligned && activation?.activeVersion ? <p className="dm-guidance">Action recommandée : matérialiser la version active v{activation.activeVersion.versionNumber}.</p> : null}
  </section>;
}

function SourcesPanel({
  location,
  hasDraft,
  openingDraft,
  openDraftError,
  onOpenDraft,
  sourcesQuery,
  revisions,
  onChanged,
  onValidate,
  validationBusy,
  activeProcessing,
  onCancelProcessing,
}: {
  location: DatasetLocation;
  hasDraft: boolean; openingDraft: boolean; openDraftError: unknown; onOpenDraft: () => void;
  sourcesQuery: { data: Awaited<ReturnType<typeof getSources>> | undefined; error: unknown; isLoading: boolean };
  revisions: Awaited<ReturnType<typeof getRevisions>>; onChanged: () => Promise<void>;
  onValidate?: () => void; validationBusy: boolean;
  activeProcessing: JobHistoryItem | null; onCancelProcessing?: () => void;
}) {
  if (!hasDraft) return <section className="dm-panel">
    <h2>Sources</h2>
    <EmptyState
      title="Aucune source n’a encore été ajoutée."
      explanation="Commencez par créer une révision et importer les fichiers raw nécessaires au dataset."
      actionLabel={openingDraft ? "Préparation en cours…" : "Ajouter des données"}
      actionDisabled={openingDraft}
      onAction={onOpenDraft}
    />
    {revisions.length ? <Link className="btn btn-outline" to={`/clients/${encodeURIComponent(location.clientId)}/datasets/${encodeURIComponent(location.datasetId)}/source-explorer`}>Consulter les révisions existantes</Link> : null}
    {openDraftError ? <p className="dm-error" role="alert">{safeErrorMessage(openDraftError)}</p> : null}
  </section>;
  if (sourcesQuery.isLoading) return <PageState message="Chargement des sources…" />;
  if (sourcesQuery.error || !sourcesQuery.data) return <PageState message={safeErrorMessage(sourcesQuery.error)} error />;
  return <section className="dm-panel">
    <h2>Sources de la préparation</h2>
    <p>Chaque action crée une nouvelle révision immuable ; les anciennes restent consultables.</p>
    <SourceExplorer location={location} data={sourcesQuery.data} revisions={revisions} onChanged={onChanged} onValidate={onValidate} validationBusy={validationBusy} activeProcessing={activeProcessing} onCancelProcessing={onCancelProcessing} />
  </section>;
}

function RevisionHistory({ revisions }: { revisions: Awaited<ReturnType<typeof getRevisions>> }) {
  return <div className="dm-revisions"><h3>Historique des révisions</h3>{revisions.length ? revisions.map((revision) => <details key={revision.id ?? revision.sourceRevision}><summary>Révision r{revision.sourceRevision} · {revision.fileCount ?? revision.files.length} fichier(s) · {formatDate(revision.capturedAt)}</summary>{revision.files.length ? <ul>{revision.files.map((file) => <li key={file.sourceId}>{file.originalFilename} · {formatBytes(Number(file.sizeBytes))} · checksum {file.checksumVerified ? "vérifié" : "non vérifié"}</li>)}</ul> : <p>Ouvrez l’explorateur pour consulter cette révision.</p>}</details>) : <Empty text="Aucune révision capturée par un job." />}</div>;
}

function ValidationPanel({ validation, activeJob: job, validationRunning, busy, hasReadyRevision, onNavigate, onValidate, onCancel }: {
  validation: Awaited<ReturnType<typeof getValidation>> | undefined;
  activeJob: JobHistoryItem | null;
  validationRunning: boolean;
  busy: boolean;
  hasReadyRevision: boolean;
  onNavigate: (section: Section) => void;
  onValidate: () => void;
  onCancel: (id: string) => void;
}) {
  let content = <EmptyState
    title="Aucune révision exploitable à valider."
    explanation="Ajoutez puis envoyez au moins un fichier raw dans la section Sources."
    actionLabel="Aller aux sources"
    onAction={() => onNavigate("sources")}
  />;
  if (hasReadyRevision && !validation) {
    content = <EmptyState
      title="Cette révision n’a pas encore été validée."
      explanation="Lancez la validation pour vérifier les fichiers raw et les règles du template."
    />;
  }
  if (hasReadyRevision && validation) {
    const successMessage = validationSuccessMessage(validation);
    content = <><div className="dm-summary-grid"><Summary label="État" value={validation.status} /><Summary label="Révision" value={`r${validation.sourceRevision}`} /><Summary label="Template" value={`${validation.templateId} ${validation.templateVersion}`} /><Summary label="Date" value={formatDate(validation.completedAt ?? validation.createdAt)} /></div>{successMessage ? <p className="dm-notice">{successMessage}</p> : null}{validation.obsolete ? <p className="dm-warning">Ce résultat concerne une ancienne révision.</p> : null}<h3>Rapport fonctionnel</h3>{validation.report ? <ValidationReportView report={validation.report} sourceRevision={validation.sourceRevision} /> : <Empty text="Le rapport n’est pas encore disponible." />}</>;
  }
  if (validationRunning) {
    content = <div className="dm-empty" role="status"><strong>Nouvelle validation en cours…</strong><p>Le nouveau rapport s’affichera ici dès que la validation sera terminée.</p></div>;
  }
  return <section className="dm-panel"><div className="dm-section-heading"><div><h2>Validation</h2><p>La sécurité et les règles du template restent vérifiées côté serveur.</p></div><button className="btn btn-primary" type="button" disabled={busy || Boolean(job) || !hasReadyRevision} onClick={onValidate}>{validationRunning ? "Validation en cours…" : "Lancer la validation"}</button></div>
    {job ? <JobCard job={job} onCancel={() => onCancel(job.id)} /> : null}
    {content}
  </section>;
}

function SemanticizationPanel({ validation, currentRevision, jobs, busy, onNavigate, onSemanticize, onCancel }: { validation: Awaited<ReturnType<typeof getValidation>> | undefined; currentRevision: number | null; jobs: JobHistoryItem[]; busy: boolean; onNavigate: (section: Section) => void; onSemanticize: () => void; onCancel: (id: string) => void }) {
  const active = activeJob(jobs, "semanticize");
  const latest = jobs.find((job) => job.type === "semanticize") ?? null;
  const failed = latest?.state === "failed" ? latest : null;
  const available = validation?.usable === true && !active;
  const actionLabel = failed ? "Réessayer" : "Lancer la sémantisation";
  const obsoleteMessage = validation?.obsolete && currentRevision !== null
    ? `La validation correspond à la révision r${validation.sourceRevision}, mais le draft courant est r${currentRevision}.`
    : null;
  return <section className="dm-panel"><div className="dm-section-heading"><div><h2>Sémantisation</h2><p>Transforme la dernière validation réussie en version candidate.</p></div><button className="btn btn-primary" type="button" disabled={!available || busy} onClick={onSemanticize}>{actionLabel}</button></div>{active ? <JobCard job={active} onCancel={() => onCancel(active.id)} /> : null}{failed && !active ? <JobCard job={failed} /> : null}{!validation?.usable ? <EmptyState title={obsoleteMessage ? "Validation obsolète." : "Aucune validation exploitable."} explanation={obsoleteMessage ?? "Validez d’abord la révision en préparation sans erreur bloquante."} actionLabel="Aller à la validation" onAction={() => onNavigate("validation")} /> : null}{validation && !available && !active ? <p className="dm-guidance">La dernière validation n’est pas utilisable pour cette action.</p> : null}</section>;
}

function validationSuccessMessage(validation: Awaited<ReturnType<typeof getValidation>>) {
  if (!validation?.usable) return null;
  const warnings = validation.report?.summary.warnings ?? 0;
  if (warnings === 1) return "Validation réussie avec 1 avertissement.";
  if (warnings > 1) return `Validation réussie avec ${warnings} avertissements.`;
  return "Validation réussie sans erreur bloquante.";
}

function VersionsPanel({ versions, selected, activation, materialization, onSelect }: { versions: CandidateVersion[]; selected: CandidateVersion | null; activation: Awaited<ReturnType<typeof getActivation>> | undefined; materialization: Awaited<ReturnType<typeof getMaterialization>> | undefined; onSelect: (id: string) => void }) {
  const summary = selected?.semanticizationSummary;
  return <section className="dm-panel"><h2>Versions</h2>{versions.length ? <div className="dm-table-wrap"><table><thead><tr><th>Version</th><th>Statut</th><th>Révision</th><th>Template</th><th>Triples</th><th>États</th><th></th></tr></thead><tbody>{versions.map((version) => <tr key={version.id}><td>v{version.versionNumber}</td><td><Status value={version.status} /></td><td>{version.sourceRevision ? `r${version.sourceRevision}` : "Legacy"}</td><td>{version.templateId ?? "Non renseigné"} {version.templateVersion ?? ""}</td><td>{version.tripleCount ?? "—"}</td><td>{activation?.activeVersion?.id === version.id ? "Active " : ""}{activation?.previousVersion?.id === version.id ? "Précédente " : ""}{materialization?.materializedVersion?.id === version.id ? "Matérialisée" : ""}</td><td><button type="button" onClick={() => onSelect(version.id)}>Détails</button></td></tr>)}</tbody></table></div> : <Empty text="Aucune version candidate." />}{selected ? <article className="dm-version-detail"><h3>Détail v{selected.versionNumber}</h3><p>Package candidat : {selected.artifacts.length ? selected.artifacts.join(", ") : "non disponible"}</p><p>Taille RDF : {selected.sizeBytes ? formatBytes(Number(selected.sizeBytes)) : "non disponible"} · triples : {selected.tripleCount ?? "non disponible"}</p>{summary ? <p>Sémantisation : {summary.sourcesSemanticized} modifiée(s), {summary.sourcesUnchanged} réutilisée(s), {summary.sourcesDeleted} supprimée(s) · {formatBytes(summary.generatedRdfBytes ?? 0)} RDF générés.</p> : null}</article> : null}</section>;
}

function PublicationPanel({ versions, selected, publication, activeJob: job, canAdmin, busy, onNavigate, onSelect, onPublish, onCancel }: { versions: CandidateVersion[]; selected: CandidateVersion | null; publication: Awaited<ReturnType<typeof getPublication>> | undefined; activeJob: JobHistoryItem | null; canAdmin: boolean; busy: boolean; onNavigate: (section: Section) => void; onSelect: (id: string) => void; onPublish: (id: string) => void; onCancel: (id: string) => void }) {
  const options = versions.map((version) => ({ value: version.id, label: `v${version.versionNumber} · ${version.status}` }));
  const retry = publication?.status === "failed";
  const cleanupPending = retry && publication.cleanupState !== "completed";
  let actionLabel = "Publier dans GraphDB";
  if (retry) actionLabel = "Réessayer la publication";
  if (job) actionLabel = "Publication en cours";
  const publish = () => {
    if (!selected) return;
    onPublish(selected.id);
  };
  return <section className="dm-panel">
    <h2>Publication GraphDB</h2>
    {versions.length ? <div className="dm-field"><span id="publication-version-label">Version candidate</span><AppSelect value={selected?.id ?? ""} options={options} onValueChange={onSelect} ariaLabelledBy="publication-version-label" /></div> : null}
    {selected ? <div className="dm-summary-grid"><Summary label="Version" value={`v${selected.versionNumber}`} /><Summary label="Publication" value={publication?.status ?? "Non demandée"} /><Summary label="Vérification" value={publication?.verified ? "Vérifiée" : "Non vérifiée"} /><Summary label="Triples publiés" value={publication?.tripleCount ?? "—"} /></div> : <EmptyState title="Aucune version candidate à publier." explanation="Terminez la sémantisation pour produire une version candidate vérifiée." actionLabel="Aller à la sémantisation" onAction={() => onNavigate("semanticization")} />}
    {job ? <JobCard job={job} onCancel={() => onCancel(job.id)} /> : publication?.job ? <JobCard job={publication.job} /> : null}
    {publication?.errorCode && !job ? <p className="dm-error">Échec sûr : {publication.errorCode}</p> : null}
    {retry && selected && !job ? <p className="dm-guidance">La candidate v{selected.versionNumber} sera réutilisée sans régénérer ses artefacts.</p> : null}
    {job && selected ? <p className="dm-guidance">La publication de la candidate v{selected.versionNumber} est en cours sans régénération de ses artefacts.</p> : null}
    {cleanupPending && !job ? <p className="dm-guidance">Le nettoyage de la tentative précédente doit se terminer avant de réessayer.</p> : null}
    {canAdmin ? <button className="btn btn-primary" type="button" disabled={busy || Boolean(job) || !selected || !publication?.publishable} onClick={publish}>{actionLabel}</button> : null}
    {canAdmin && selected && !publication?.publishable && !retry && !job ? <p className="dm-guidance">La version doit être candidate, complète et non publiée avant cette action.</p> : null}
  </section>;
}

function ActivationPanel({ versions, activation, materialization, activeMaterialization, canAdmin, busy, onNavigate, onActivate, onMaterialize, onCancel }: { versions: CandidateVersion[]; activation: Awaited<ReturnType<typeof getActivation>> | undefined; materialization: Awaited<ReturnType<typeof getMaterialization>> | undefined; activeMaterialization: JobHistoryItem | null; canAdmin: boolean; busy: boolean; onNavigate: (section: Section) => void; onActivate: (id: string) => void; onMaterialize: () => void; onCancel: (id: string) => void }) {
  const activatable = versions.filter((version) => version.status === "published" && version.id !== activation?.activeVersion?.id);
  const [targetId, setTargetId] = useState("");
  const activationOptions = activatable.map((version) => ({ value: version.id, label: `v${version.versionNumber}` }));
  const activeJob = activeMaterialization ?? materialization?.activeJob ?? null;
  const materializationJob = materializationJobCard(activeMaterialization, materialization?.activeJob ?? materialization?.lastJob);
  const actionLabel = materializationActionLabel(materialization, Boolean(activeJob));
  const actionDisabled = busy || !materialization?.canMaterialize || Boolean(activeJob);
  return <section className="dm-panel"><h2>Activation et matérialisation</h2><div className="dm-alignment"><Summary label="Version active PostgreSQL" value={activation?.activeVersion ? `v${activation.activeVersion.versionNumber}` : "Aucune"} /><Summary label="Version précédente" value={activation?.previousVersion ? `v${activation.previousVersion.versionNumber}` : "Aucune"} /><Summary label="Version matérialisée" value={materialization?.materializedVersion?.versionNumber ? `v${materialization.materializedVersion.versionNumber}` : "Aucune"} /><Summary label="État" value={alignmentLabel(materialization)} /></div>{materializationJob ? <JobCard job={materializationJob} onCancel={activeJob ? () => onCancel(activeJob.id) : undefined} /> : null}{activeJob && materialization?.materializedVersion ? <p className="dm-guidance">La matérialisation existante reste utilisée jusqu’à la réussite complète de la rematérialisation.</p> : null}{!activation?.activeVersion && !activatable.length ? <EmptyState title="Aucune version publiée à activer." explanation="Publiez d’abord une version candidate dans GraphDB." actionLabel="Aller à la publication" onAction={() => onNavigate("publication")} /> : null}{canAdmin ? <div className="dm-actions-stack"><div><div className="dm-field"><span id="activation-version-label">Version publiée à activer</span><AppSelect value={targetId} options={activationOptions} onValueChange={setTargetId} ariaLabelledBy="activation-version-label" placeholder="Sélectionner une version" disabled={!activationOptions.length} /></div><button className="btn btn-outline" type="button" disabled={!targetId || busy} onClick={() => onActivate(targetId)}>Activer cette version</button><p>{activationOptions.length ? "L’activation change la source de vérité PostgreSQL sans matérialiser automatiquement." : "Une version publiée est nécessaire avant l’activation."}</p></div><div><button className="btn btn-primary" type="button" disabled={actionDisabled} onClick={onMaterialize}>{actionLabel}</button><p>{activation?.activeVersion ? "Cette opération génère les artefacts utilisés par les lectures historiques." : "Activez d’abord une version publiée pour rendre cette action disponible."}</p></div></div> : null}</section>;
}

function JobsPanel({ jobs, history, loading }: { jobs: JobHistoryItem[]; history: Awaited<ReturnType<typeof getHistory>>["items"]; loading: boolean }) {
  if (loading) return <PageState message="Chargement de l’historique…" />;
  return <section className="dm-panel"><h2>Historique des jobs</h2>{jobs.length ? <div className="dm-job-list">{jobs.map((job) => <JobCard key={job.id} job={job} />)}</div> : <Empty text="Aucun job." />}<h3>Chronologie métier</h3>{history.length ? <ol className="dm-timeline">{history.map((item) => <li key={item.id}><strong>{item.action}</strong> · {item.outcome}<span>{item.actorName ?? "Système"} · {formatDate(item.createdAt)}</span></li>)}</ol> : <Empty text="Aucun événement d’audit." />}</section>;
}

type JobCardData = { id: string; type: string; state: string; step: string | null; progress: number; outcome?: string | null; requestedBy?: string | null; errorCode: string | null; errorMessage: string | null; createdAt: string; updatedAt: string; startedAt?: string | null; finishedAt?: string | null };

function JobCard({ job, onCancel }: { job: JobCardData; onCancel?: () => void }) {
  const active = ACTIVE_JOB_STATES.has(job.state);
  const now = useCurrentTime(active);
  const showError = Boolean(job.errorCode) && !active;
  const start = new Date(job.startedAt ?? job.createdAt).getTime();
  const end = active ? now : new Date(job.finishedAt ?? job.updatedAt).getTime();
  const duration = end - start;
  return <article className="dm-job"><div><strong>{jobTypeLabel(job.type)}</strong> <Status value={job.state} /></div><progress max="100" value={job.progress} aria-label={`Progression ${job.progress} %`} /><span>{job.progress} % {job.step ? `· ${jobStepLabel(job.step)}` : ""}</span><span className="dm-job-duration">{active ? "Temps écoulé" : "Durée"} : {formatDuration(duration)}</span>{job.outcome ? <span>Résultat : {job.outcome}</span> : null}{job.requestedBy ? <span>Demandé par {job.requestedBy}</span> : null}{onCancel ? <button className="btn btn-outline" type="button" onClick={onCancel}>Abandonner</button> : null}{showError ? <p className="dm-error">{job.errorCode}{job.errorMessage ? ` — ${job.errorMessage}` : ""}</p> : null}</article>;
}

function Summary({ label, value }: { label: string; value: string }) { return <div className="dm-summary"><dt>{label}</dt><dd>{value}</dd></div>; }
function Status({ value }: { value: string }) { return <span className="dm-status">{value}</span>; }
function Empty({ text }: { text: string }) { return <p className="dm-empty">{text}</p>; }
function EmptyState({ title, explanation, actionLabel, actionDisabled = false, onAction }: {
  title: string;
  explanation: string;
  actionLabel?: string;
  actionDisabled?: boolean;
  onAction?: () => void;
}) {
  return <div className="dm-empty-state">
    <strong>{title}</strong>
    <p>{explanation}</p>
    {actionLabel && onAction ? <button className="btn btn-primary" type="button" disabled={actionDisabled} onClick={onAction}>{actionLabel}</button> : null}
  </div>;
}
function PageState({ message, error = false }: { message: string; error?: boolean }) { return <main className="dm-page"><p className={error ? "dm-error" : "dm-empty"} role={error ? "alert" : "status"}>{message}</p></main>; }

function validSection(value: string | null): Section {
  const match = SECTIONS.find(([id]) => id === value);
  return match?.[0] ?? "overview";
}
function isTerminalDraftStatus(status: string | undefined) {
  return status === "published" || status === "abandoned";
}
function findDraftRevision(
  revisions: SourceRevision[],
  draft: DataManagementContext["draft"],
) {
  if (!draft) return null;
  for (const revision of revisions) {
    if (!revision.id) continue;
    if (revision.draftNumber !== draft.draftNumber) continue;
    if (revision.sourceRevision !== draft.sourceRevision) continue;
    return revision;
  }
  return null;
}
function workflowCompletionDuringRun(completed: Record<string, boolean>, currentStep: FullPipelineStep) {
  const progress = {
    sources: completed.sources,
    validation: false,
    semanticization: false,
    publication: false,
    activation: false,
    materialization: false,
  };
  if (currentStep === "validation") return progress;
  progress.validation = true;
  if (currentStep === "semanticization") return progress;
  progress.semanticization = true;
  if (currentStep === "publication") return progress;
  progress.publication = true;
  if (currentStep === "activation") return progress;
  progress.activation = true;
  return progress;
}
function activeJob(jobs: JobHistoryItem[] | undefined, type: string) {
  return jobs?.find((job) => job.type === type && ACTIVE_JOB_STATES.has(job.state)) ?? null;
}
function jobStepLabel(step: string) {
  const counts = step.match(/analyze_reports:(\d+)\/(\d+)/);
  if (counts) return `Analyse des sources : ${counts[1]} / ${counts[2]}`;
  if (step === "publication:load_candidate") return "Chargement du candidat";
  if (step === "publication:validate_candidate") return "Validation du candidat";
  if (step === "publication:preflight") return "Contrôle de cohérence GraphDB";
  if (step === "publication:validate_changed_artifacts") return "Validation des artefacts modifiés";
  if (step === "publication:publish_delta") return "Publication du delta GraphDB";
  if (step === "publication:publish_version_manifest") return "Publication de l’index de version";
  if (step === "publication:finalize") return "Finalisation de la publication";
  return step;
}
function jobTypeLabel(type: string) {
  if (type === "validate") return "Validation";
  if (type === "semanticize") return "Sémantisation";
  if (type === "publish") return "Publication";
  if (type === "materialize") return "Matérialisation";
  return type;
}
function finishCurrentTiming(current: PipelineTimings, nextStep: FullPipelineStep | null, changedAt: number) {
  const updated = { ...current };
  let currentStep: FullPipelineStep | undefined;
  for (const step of PIPELINE_STEPS) {
    if (current[step]?.durationMs !== null) continue;
    currentStep = step;
    break;
  }
  if (currentStep && currentStep !== nextStep) {
    const timing = current[currentStep];
    if (timing) updated[currentStep] = { ...timing, durationMs: changedAt - timing.startedAt };
  }
  if (nextStep && !updated[nextStep]) {
    updated[nextStep] = { startedAt: changedAt, durationMs: null };
  }
  return updated;
}
function useCurrentTime(active: boolean) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, [active]);
  return now;
}
function formatDuration(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1_000));
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);
  const clock = `${`${minutes}`.padStart(2, "0")}:${`${seconds}`.padStart(2, "0")}`;
  if (hours === 0) return clock;
  return `${`${hours}`.padStart(2, "0")}:${clock}`;
}
function materializationJobCard(active: JobHistoryItem | null, last: MaterializationJob | null | undefined): JobCardData | null {
  if (active) return active;
  if (!last) return null;
  return {
    id: last.id,
    type: "materialize",
    state: last.state,
    step: null,
    progress: last.progress,
    outcome: last.outcome,
    errorCode: last.errorCode,
    errorMessage: null,
    createdAt: last.requestedAt,
    updatedAt: last.finishedAt ?? last.requestedAt,
    startedAt: last.startedAt,
    finishedAt: last.finishedAt,
  };
}
function actionStartedMessage(kind: string, rematerialization = false) {
  if (kind === "validate") return "Validation démarrée.";
  if (kind === "semanticize") return "Sémantisation démarrée.";
  if (kind === "publish") return "Publication démarrée.";
  if (kind === "activate") return "Activation terminée.";
  return rematerialization ? "Rematérialisation démarrée." : "Matérialisation démarrée.";
}
function requiresConfirmation(kind: string) { return ["publish", "activate", "materialize"].includes(kind); }
function confirmationMessage(kind: string) {
  if (kind === "publish") return "Publier cette version dans GraphDB ?";
  if (kind === "activate") return "Activer cette version comme source de vérité PostgreSQL ? La matérialisation restera séparée.";
  return "Lancer cette opération ?";
}
function materializationConfirmation(value: MaterializationState | undefined) {
  const version = value?.activeVersion?.versionNumber;
  if (value?.canRematerialize && version) {
    return `La version v${version} sera rematérialisée avec le matérialiseur actuel.\nLa matérialisation existante restera active jusqu’à la réussite complète.`;
  }
  return "Lancer la matérialisation de la version active ?";
}
function materializationActionLabel(value: MaterializationState | undefined, active: boolean) {
  if (active && value?.materializedVersion) return "Rematérialisation en cours";
  if (active) return "Matérialisation en cours";
  if (value?.canRematerialize) return "Rematérialiser la version active";
  if (value?.canRetry) return "Relancer la matérialisation";
  return "Matérialiser la version active";
}
function alignmentLabel(value: Awaited<ReturnType<typeof getMaterialization>> | undefined) {
  if (!value?.activeVersion) return "Aucun dataset actif";
  if (value.status === "rematerializing") return "Rematérialisation en cours";
  if (value.status === "aligned") return "Alignée";
  if (value.lastJob?.state === "queued") return "Matérialisation en attente";
  if (value.lastJob?.state === "running") return "Matérialisation en cours";
  if (value.lastJob?.outcome === "superseded") return "Matérialisation obsolète";
  if (value.lastJob?.state === "failed") return "Matérialisation échouée";
  if (value.materializedVersion) return "Ancienne version encore servie";
  return "Active mais non matérialisée";
}
function validateFiles(files: File[], policy: { maxFiles: number; maxBytesPerFile: number; allowedExtensions: string[] }) {
  if (files.length > policy.maxFiles) return `Sélection limitée à ${policy.maxFiles} fichiers.`;
  const oversized = files.find((file) => file.size > policy.maxBytesPerFile);
  if (oversized) return `${oversized.name} dépasse la taille autorisée.`;
  const invalid = files.find((file) => !policy.allowedExtensions.some((extension) => file.name.toLowerCase().endsWith(extension.toLowerCase())));
  if (invalid) return `${invalid.name} utilise une extension non autorisée.`;
  return "";
}
function formatBytes(value: number) { if (!Number.isFinite(value)) return "—"; if (value < 1024) return `${value} o`; return `${(value / 1024 / 1024).toFixed(2)} Mo`; }
function formatDate(value: string) { return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }
